import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { api, ApiError } from "../../lib/api.js";
import { Arrow, Clock, Info, Linkedin, Mail, ShieldCheck, User, Users, Video } from "../../components/icons.js";

/**
 * Candidate · step 1 of 5 ("Your details") — candidate information (POST /invite/{token}/info).
 *
 * Name/email prefill from the invite and are editable — a recruiter-entered address can be
 * stale or mistyped, and this is the candidate's chance to correct it before anything
 * (consent, identity verification) runs against it. LinkedIn is always optional and purely
 * informational; leaving it blank never blocks continuing.
 */
export function CandidateInfoStep({
  token,
  orgName,
  requisitionTitle,
  candidateName,
  email,
  linkedinUrl,
  idvEnabled,
  idvMethod,
  onContinue,
}: {
  token: string;
  orgName: string;
  requisitionTitle: string;
  candidateName: string | null;
  email: string | null;
  linkedinUrl: string | null;
  idvEnabled: boolean;
  idvMethod?: "id_document" | "otp";
  onContinue: () => void;
}): ReactNode {
  // An email code is only sent at check-in when IDV runs in OTP mode. Otherwise this step just
  // collects contact details, so the copy must not promise a "verification code" that never comes.
  const otpAtCheckin = idvEnabled && idvMethod === "otp";
  const [name, setName] = useState(candidateName ?? "");
  const [emailVal, setEmailVal] = useState(email ?? "");
  const [linkedin, setLinkedin] = useState(linkedinUrl ?? "");

  const save = useMutation({
    mutationFn: () =>
      api.post<unknown>(`/invite/${token}/info`, {
        candidateName: name.trim() || undefined,
        email: emailVal.trim() || undefined,
        linkedinUrl: linkedin.trim(),
      }),
    onSuccess: () => onContinue(),
  });

  const errorMsg =
    save.isError && save.error instanceof ApiError && save.error.status === 400
      ? "Please check your name and email — one of them isn't valid."
      : save.isError
        ? "Couldn't save your info. Please retry in a moment."
        : null;

  const canSubmit = name.trim().length > 0 && /\S+@\S+\.\S+/.test(emailVal.trim());

  return (
    <div className="cx-step t1c-info">
      {/* What this is, before anything is asked — role, company, time, what you'll need. Replaces
          the stock illustration that pushed the form below the fold on phones (audit 42 / top-15 #1). */}
      <div className="t1c-invite">
        <div className="t1c-invite-eyebrow">Interview invitation</div>
        <div className="t1c-invite-role" title={requisitionTitle}>{requisitionTitle}</div>
        <div className="t1c-invite-org">at {orgName}</div>
        <ul className="t1c-invite-facts">
          <li><Clock /> About 30 minutes</li>
          <li><Video /> Camera &amp; mic</li>
          <li><Users /> AI interviewer · a person decides</li>
        </ul>
      </div>

      <h1 className="t1c-h1">Your details</h1>
      <p className="mut t1c-lede">
        {otpAtCheckin ? (
          <>Check these, then continue. At check-in we'll email a one-time code here — it confirms you control this inbox, not your identity.</>
        ) : (
          <>Check these, then continue — correct anything that's wrong.</>
        )}
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit && !save.isPending) save.mutate();
        }}
        noValidate
      >
        <div className="field t1c-field">
          <label htmlFor="cx-name">
            Full name <span className="t1c-req" aria-hidden="true">*</span>
          </label>
          <div className="t1c-input-ico">
            <User />
            <input id="cx-name" className="input" autoComplete="name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Priya Sharma" />
          </div>
        </div>

        <div className="field t1c-field">
          <label htmlFor="cx-email">
            Email <span className="t1c-req" aria-hidden="true">*</span>
          </label>
          <div className="t1c-input-ico">
            <Mail />
            <input id="cx-email" className="input" type="email" inputMode="email" autoComplete="email" required value={emailVal} onChange={(e) => setEmailVal(e.target.value)} placeholder="you@example.com" />
          </div>
        </div>

        <div className="field t1c-field">
          <label htmlFor="cx-linkedin">
            LinkedIn profile <span className="t1c-opt">(optional)</span>
          </label>
          <div className="t1c-input-ico">
            <Linkedin />
            <input id="cx-linkedin" className="input" type="url" inputMode="url" value={linkedin} onChange={(e) => setLinkedin(e.target.value)} placeholder="linkedin.com/in/your-name" />
          </div>
        </div>

        {errorMsg && (
          <div className="banner crit" style={{ marginBottom: 16 }}>
            <Info />
            <span>{errorMsg}</span>
          </div>
        )}

        <button type="submit" className="btn primary t1c-cta" disabled={!canSubmit || save.isPending}>
          {save.isPending ? "Saving…" : "Continue"} <Arrow />
        </button>
        <p className="t1c-fine">
          <span className="t1c-req" aria-hidden="true">*</span> Required. <ShieldCheck /> Your link is personal — don't share it.
        </p>
      </form>
    </div>
  );
}
